import type { KitFontId, ThemeTypeStyle } from '../releaseThemes';

/**
 * The font metrics of the kit fonts and the text fit of the renderer and of the web previews: one
 * copy, so a browser preview wraps a line where the render wraps it. Pure; no DOM measure.
 */

/**
 * The mean advance widths of a kit font, in em, at its display weight (`KIT_FONT_FACES`
 * `displayWeight`). Measured with fontTools from the font files in `src/video/static/fonts/`:
 * `lower` and `upper` are the letters weighted by their English frequency, `punct` is the mean of
 * `, . ' ! ? -`.
 */
export interface KitFontMetrics {
  lower: number;
  upper: number;
  space: number;
  digit: number;
  punct: number;
}

export const KIT_FONT_METRICS: Readonly<Record<KitFontId, KitFontMetrics>> = {
  grotesk: { lower: 0.512, upper: 0.643, space: 0.199, digit: 0.598, punct: 0.369 },
  serif: { lower: 0.559, upper: 0.734, space: 0.212, digit: 0.621, punct: 0.354 },
  mono: { lower: 0.6, upper: 0.6, space: 0.6, digit: 0.6, punct: 0.6 },
  hand: { lower: 0.366, upper: 0.519, space: 0.242, digit: 0.449, punct: 0.237 },
  condensed: { lower: 0.445, upper: 0.454, space: 0.234, digit: 0.478, punct: 0.285 },
  italic: { lower: 0.397, upper: 0.477, space: 0.17, digit: 0.389, punct: 0.289 },
  didone: { lower: 0.503, upper: 0.663, space: 0.239, digit: 0.503, punct: 0.332 },
  script: { lower: 0.597, upper: 0.721, space: 0.2, digit: 0.689, punct: 0.41 },
  wide: { lower: 0.728, upper: 0.874, space: 0.242, digit: 0.828, punct: 0.393 },
};

/** The line height of one line of text (`ReelTextLayer`). */
export const LINE_HEIGHT = 1.08;
/** The line height of a title with the accent line: the two lines sit close, as in the design tile (0.86 to 1.02). */
export const TWO_LINE_HEIGHT = 0.94;

/**
 * The share of the box width that a fitted line may use. The widths are means, so a line of wide
 * letters can be a little longer than the estimate: the rest of the box absorbs it.
 */
export const FIT_WIDTH_SHARE = 0.92;
/** A fitted text takes at most this many lines. */
export const FIT_MAX_LINES = 4;

/** The case and the letter spacing (em) that change the width of a text. */
export type FitTextStyle = Pick<ThemeTypeStyle, 'case' | 'tracking'>;

function charWidthEm(char: string, metrics: KitFontMetrics, textCase: FitTextStyle['case']): number {
  if (char === ' ') {
    return metrics.space;
  }
  if (/[0-9]/.test(char)) {
    return metrics.digit;
  }
  const lower = char.toLowerCase();
  const upper = char.toUpperCase();
  if (lower === upper) {
    // Not a letter: punctuation and signs.
    return metrics.punct;
  }
  if (textCase === 'uppercase') {
    return metrics.upper;
  }
  if (textCase === 'lowercase') {
    return metrics.lower;
  }
  return char === upper ? metrics.upper : metrics.lower;
}

/** The width of a text at a font size of 1 px, with the case and the letter spacing of the role. Pure. */
export function textWidthEm(text: string, fontId: KitFontId, style: FitTextStyle): number {
  const metrics = KIT_FONT_METRICS[fontId];
  const chars = Array.from(text);
  const letters = chars.reduce((sum, char) => sum + charWidthEm(char, metrics, style.case), 0);
  return letters + style.tracking * chars.length;
}

/**
 * The lines of a text that wraps at `maxWidthEm` (in em of the font), word by word, as the browser
 * wraps it. A word that is wider than a line stays on a line of its own. Pure.
 */
export function wrapWords(text: string, fontId: KitFontId, style: FitTextStyle, maxWidthEm: number): string[] {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current === '' ? word : `${current} ${word}`;
    if (current !== '' && textWidthEm(candidate, fontId, style) > maxWidthEm) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current !== '') {
    lines.push(current);
  }
  return lines;
}

export interface FitTextInput {
  text: string;
  fontId: KitFontId;
  style: FitTextStyle;
  boxWidthPx: number;
  boxHeightPx: number;
  /** The line height of the text layer (`LINE_HEIGHT`, or `TWO_LINE_HEIGHT` with the accent line). */
  lineHeight: number;
  /** 2 when the title repeats on an accent line (Hype, Neon): the block is two copies high. */
  copies?: 1 | 2;
  /** The largest size, in px. */
  maxSizePx: number;
  /** The smallest size, in px. A text that does not fit even at this size gets it. */
  minSizePx?: number;
  maxLines?: number;
}

export interface FitTextResult {
  sizePx: number;
  lines: string[];
  /** The height of the text block at `sizePx`, all copies included. */
  blockHeightPx: number;
}

/**
 * The fit-to-box rule of the hero text of a text-led layout (a lyric card, a type-only story): the
 * largest size at which the text wraps into at most `maxLines` lines (default 4), no line is wider
 * than `FIT_WIDTH_SHARE` of the box, and the block (all copies) fits the box height. A long line
 * thus wraps into 2 to 4 lines that fill most of the box width; a short word grows until the box
 * height or `maxSizePx` stops it. Pure.
 */
export function fitTextSize(input: FitTextInput): FitTextResult {
  const copies = input.copies ?? 1;
  const maxLines = input.maxLines ?? FIT_MAX_LINES;
  const minSize = Math.max(1, input.minSizePx ?? 8);
  const budget = input.boxWidthPx * FIT_WIDTH_SHARE;
  const layoutAt = (size: number): { lines: string[]; fits: boolean; height: number } => {
    const lines = wrapWords(input.text, input.fontId, input.style, budget / size);
    const widest = Math.max(0, ...lines.map((line) => textWidthEm(line, input.fontId, input.style) * size));
    const height = lines.length * copies * size * input.lineHeight;
    return {
      lines,
      height,
      fits: lines.length <= maxLines && widest <= budget + 0.01 && height <= input.boxHeightPx + 0.01,
    };
  };
  let low = minSize;
  let high = Math.max(minSize, input.maxSizePx);
  if (layoutAt(high).fits) {
    low = high;
  } else {
    // The fit gets better as the size goes down, so a binary search finds the largest size.
    for (let step = 0; step < 32 && high - low > 0.05; step += 1) {
      const middle = (low + high) / 2;
      if (layoutAt(middle).fits) {
        low = middle;
      } else {
        high = middle;
      }
    }
  }
  const sizePx = Math.floor(low * 100) / 100;
  const result = layoutAt(sizePx);
  return { sizePx, lines: result.lines, blockHeightPx: Math.round(result.height * 100) / 100 };
}
